import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  COMPOSITION_BUFFER_MODE,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { finalize, of } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'

import { ButtonComponent } from '../../../../components/button/button.component'
import {
  Option,
  SelectComponent,
} from '../../../../components/select/select.component'
import { LoadingComponent } from '../../../../components/loading/loading.component'
import {
  ExternalIntegrationService,
  SaveKaikuraKeyParam,
} from '../../../../services/external-integration/external-integration.service'
import { KaikuraIntegrationSetting } from '../../../../services/external-integration/external-integration.mapping'
import { SkillMap } from '../../../../services/assessment/assessment.mapping'
import { AssessmentStore } from '../../../../stores/assessment.store'
import {
  CustomError,
  CustomErrorCode,
} from '../../../../util/throwIfCustomError'

export type KaikuraIntegrationData = {
  setting: KaikuraIntegrationSetting | null
}

export type KaikuraIntegrationResult = KaikuraIntegrationSetting
const KAIKURA_SERVER_NAME_PATTERN =
  /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/
const KAIKURA_ID_PATTERN = /^[A-Z0-9-]+$/
const KAIKURA_CREDENTIAL_PARTIAL_ERROR_MESSAGE =
  '3 つ全て入力するか全て空欄にしてください'

@UntilDestroy()
@Component({
  selector: 'app-kaikura-integration',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatIcon,
    ButtonComponent,
    SelectComponent,
    LoadingComponent,
  ],
  templateUrl: './kaikura-integration.component.html',
  styleUrls: ['./kaikura-integration.component.scss'],
  // IME の composition 中もタグ入力値を FormControl に反映し、追加ボタンを即時更新する。
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: COMPOSITION_BUFFER_MODE, useValue: false }],
})
export class KaikuraIntegrationComponent implements OnInit {
  // backend `kaikuraService.ts:KAIKURA_SERVER_NAME_REGEX` と同じパターン。
  // 先頭/末尾はハイフン不可、中央のみハイフン可。
  serverName = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.pattern(KAIKURA_SERVER_NAME_PATTERN),
    ],
  })
  id = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(KAIKURA_ID_PATTERN)],
  })
  secret = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  })
  tagInput = new FormControl('', { nonNullable: true })
  trimmedTagInput = ''

  formGroup = this.formBuilder.group({
    serverName: this.serverName,
    id: this.id,
    secret: this.secret,
  })

  skillMaps: SkillMap[] = []
  skillMapOptions: Option[] = []
  selectedSkillMap: Option = { id: '', name: '' }
  tagAllowlist: string[] = []
  isEditMode = false
  isSubmitted = false
  isLoading = false
  errorMessage: string | null = null
  isSecretVisible = false

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: KaikuraIntegrationData,
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<KaikuraIntegrationComponent>,
    private snackBar: MatSnackBar,
    private externalIntegrationService: ExternalIntegrationService,
    private assessmentStore: AssessmentStore,
  ) {}

  ngOnInit(): void {
    const setting = this.data.setting
    this.isEditMode = setting?.active === true
    this.configureCredentialValidators()
    this.tagAllowlist = [...(setting?.tagAllowlist ?? [])]

    this.tagInput.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.trimmedTagInput = value.trim()
    })

    this.isLoading = true
    this.assessmentStore
      .getSkillMaps()
      .pipe(
        tap((skillMaps) => {
          this.skillMaps = skillMaps
          this.skillMapOptions = skillMaps.map((skillMap) => ({
            id: skillMap.id,
            name: skillMap.name,
          }))
          const selected = this.skillMapOptions.find(
            (option) => option.id === setting?.skillMapId,
          )
          if (selected) {
            this.selectedSkillMap = selected
          }
        }),
        catchError(() => {
          this.errorMessage =
            'スキルマップの取得に失敗しました。時間をおいて再度お試しください。'
          return of([])
        }),
        finalize(() => {
          this.isLoading = false
        }),
      )
      .subscribe()
  }

  onSubmit(event: Event): void {
    event.preventDefault()
    this.isSubmitted = true
    this.errorMessage = null
    this.formGroup.markAllAsTouched()

    const credentials = this.getTrimmedCredentials()
    const credentialInputCount = Object.values(credentials).filter(
      (value) => value !== '',
    ).length

    if (
      this.isEditMode &&
      credentialInputCount !== 0 &&
      credentialInputCount !== 3
    ) {
      this.errorMessage = KAIKURA_CREDENTIAL_PARTIAL_ERROR_MESSAGE
      return
    }

    if (this.isSaveDisabled()) {
      return
    }

    const param: SaveKaikuraKeyParam = {
      skillMapId: this.selectedSkillMap.id,
      tagAllowlist: this.tagAllowlist,
    }

    if (credentialInputCount === 3) {
      param.serverName = credentials.serverName
      param.id = credentials.id
      param.secret = credentials.secret
    }

    this.isLoading = true
    this.externalIntegrationService
      .saveKaikuraKey(param)
      .pipe(
        tap(() => {
          this.snackBar.open('カイクラ連携設定を保存しました。', '閉じる')
          this.dialogRef.close({
            active: true,
            tagAllowlist: param.tagAllowlist,
            skillMapId: param.skillMapId,
            lastSyncedUpdateDatetime:
              this.data.setting?.lastSyncedUpdateDatetime ?? null,
          } satisfies KaikuraIntegrationResult)
        }),
        catchError((error) => {
          this.errorMessage = this.toErrorMessage(error)
          return of(null)
        }),
        finalize(() => {
          this.isLoading = false
        }),
      )
      .subscribe()
  }

  addTag(): void {
    const tag = this.tagInput.value.trim()
    if (!tag) {
      this.tagInput.setValue('')
      return
    }

    if (this.tagAllowlist.includes(tag)) {
      return
    }

    this.tagAllowlist = [...this.tagAllowlist, tag]
    this.tagInput.setValue('')
  }

  removeTag(tag: string): void {
    this.tagAllowlist = this.tagAllowlist.filter((value) => value !== tag)
  }

  changeSkillMap(option: Option): void {
    this.selectedSkillMap = this.skillMapOptions.find(
      (op) => op.id === option.id,
    ) ?? {
      id: '',
      name: '',
    }
  }

  isSaveDisabled(): boolean {
    return (
      this.isLoading ||
      this.formGroup.invalid ||
      !this.isSkillMapSelected() ||
      !this.hasTagAllowlist()
    )
  }

  isSkillMapSelected(): boolean {
    return this.selectedSkillMap.id !== ''
  }

  hasTagAllowlist(): boolean {
    return this.tagAllowlist.length > 0
  }

  isTagDuplicated(): boolean {
    return (
      this.trimmedTagInput !== '' &&
      this.tagAllowlist.includes(this.trimmedTagInput)
    )
  }

  isAddTagDisabled(): boolean {
    return this.trimmedTagInput === '' || this.isTagDuplicated()
  }

  close(): void {
    this.dialogRef.close()
  }

  private toErrorMessage(error: unknown): string {
    if (error instanceof CustomError) {
      if (error.code === CustomErrorCode.INVALID_KAIKURA_KEYS) {
        return 'キーが無効です。環境 ID と秘密の文字列を確認してください。'
      }

      if (error.code === CustomErrorCode.KAIKURA_CONNECTION_ERROR) {
        return 'サーバ名が誤っている可能性があります。'
      }
    }

    return 'カイクラ連携設定の保存に失敗しました。時間をおいて再度お試しください。'
  }

  private configureCredentialValidators(): void {
    this.serverName.setValidators(
      this.isEditMode
        ? [Validators.pattern(KAIKURA_SERVER_NAME_PATTERN)]
        : [
            Validators.required,
            Validators.pattern(KAIKURA_SERVER_NAME_PATTERN),
          ],
    )
    this.id.setValidators(
      this.isEditMode
        ? [Validators.pattern(KAIKURA_ID_PATTERN)]
        : [Validators.required, Validators.pattern(KAIKURA_ID_PATTERN)],
    )
    this.secret.setValidators(this.isEditMode ? [] : [Validators.required])
    this.serverName.updateValueAndValidity()
    this.id.updateValueAndValidity()
    this.secret.updateValueAndValidity()
  }

  private getTrimmedCredentials(): {
    serverName: string
    id: string
    secret: string
  } {
    return {
      serverName: this.serverName.value.trim(),
      id: this.id.value.trim(),
      secret: this.secret.value.trim(),
    }
  }
}
